import React, { useCallback, useRef, useState } from 'react';
import DismissIcon from './CustomIcons/DismissIcon';
import { FOCUS_RING } from './focusRing';
import { activateOnEnterOrSpace } from './keyboardActivation';
import Tooltip from './Tooltip';
import type { TextColor } from './Typography/Text';
import { getTextColor } from './Typography/textStyles';

export type TagType =
    | 'primary'
    | 'secondary'
    | 'default'
    | 'error'
    | 'warning'
    | 'success'
    | 'emphasis'
    | 'info'
    /** Marks content a machine produced rather than a person — provenance, not severity. */
    | 'assistant';

export type WidthSize = 'large' | 'medium' | 'small' | 'xsmall';

export type PaddingSize = 'default' | 'compact';

type TagFontSize = 'xsmall' | 'small' | 'medium';

interface TagProps {
    value: string | number | React.ReactNode;
    /** Defaults to 12px. Use `xsmall` for a secondary badge beside a primary one. */
    fontSize?: TagFontSize;
    type?: TagType;
    bordered?: boolean;
    shadow?: boolean;
    textTransform?: 'none' | 'uppercase';
    width?: WidthSize;
    paddingSize?: PaddingSize;
    icon?: React.ReactNode;
    iconPosition?: 'left' | 'right';
    iconColor?: TextColor;
    pointerType?: 'pointer' | 'default';
    onClick?: () => void;
    onRemove?: (e: React.MouseEvent) => void;
    className?: string;
}

const getFontSizeClass = (fontSize?: TagFontSize): string => {
    switch (fontSize) {
        case 'xsmall':
            return 'text-[length:var(--malachite-font-size-xsmall)]';
        case 'small':
            return 'text-[length:var(--malachite-font-size-small)]';
        case 'medium':
            return 'text-[length:var(--malachite-font-size-medium)]';
        default:
            return 'text-xs';
    }
};

const getWidthClasses = (width?: WidthSize): string => {
    switch (width) {
        case 'large':
            return 'w-[88px]';
        case 'medium':
            return 'w-[44px]';
        case 'small':
            return 'w-[24px]';
        case 'xsmall':
            return 'w-[20px]';
        default:
            return 'w-fit';
    }
};

const getPaddingClasses = (paddingSize?: PaddingSize): string => {
    return paddingSize === 'compact' ? 'px-[6px] py-[1px]' : 'px-2 py-1';
};

const getBorderRadiusClasses = (paddingSize?: PaddingSize): React.CSSProperties => {
    return {
        borderRadius:
            paddingSize === 'compact' ? 'var(--malachite-radius-md)' : 'var(--malachite-radius-lg)',
    };
};

const getTypeClasses = (
    type: TagType,
    paddingSize?: PaddingSize,
    fontSize?: TagFontSize,
) => {
    const baseClasses = `inline-flex items-center justify-center ${getPaddingClasses(paddingSize)} ${getFontSizeClass(fontSize)} font-medium leading-normal tracking-[0.11px]`;

    const typeStyles = {
        primary: {
            bg: 'bg-[var(--malachite-tag-primary-background)]',
            text: 'text-[var(--malachite-tag-primary-foreground)]',
        },
        secondary: {
            bg: 'bg-[var(--malachite-tag-secondary-background)]',
            text: 'text-[var(--malachite-tag-secondary-foreground)]',
        },
        default: {
            bg: 'bg-[var(--malachite-tag-default-background)]',
            text: 'text-[var(--malachite-tag-default-foreground)]',
        },
        error: {
            bg: 'bg-[var(--malachite-tag-error-background)]',
            text: 'text-[var(--malachite-tag-error-foreground)]',
        },
        warning: {
            bg: 'bg-[var(--malachite-tag-warning-background)]',
            text: 'text-[var(--malachite-tag-warning-foreground)]',
        },
        success: {
            bg: 'bg-[var(--malachite-tag-success-background)]',
            text: 'text-[var(--malachite-tag-success-foreground)]',
        },
        emphasis: {
            bg: 'bg-[var(--malachite-tag-emphasis-background)]',
            text: 'text-[var(--malachite-tag-emphasis-foreground)]',
        },
        info: {
            bg: 'bg-[var(--malachite-tag-info-background)]',
            text: 'text-[var(--malachite-tag-info-foreground)]',
        },
        assistant: {
            bg: 'bg-[var(--malachite-tag-assistant-background)]',
            text: 'text-[var(--malachite-tag-assistant-foreground)]',
        },
    };

    const style = typeStyles[type];
    const classes = [baseClasses, style.bg, style.text].filter(Boolean);

    return classes.join(' ');
};

const getTypeStyles = (
    type: TagType,
    bordered?: boolean,
    shadow?: boolean,
    paddingSize?: PaddingSize,
): React.CSSProperties => {
    const styles: React.CSSProperties = {
        ...getBorderRadiusClasses(paddingSize),
    };

    if (bordered) {
        styles.border = `2px solid var(--malachite-tag-${type}-border)`;
    }

    if (type === 'emphasis') {
        styles.boxShadow = '0px 0px 10px 0px var(--malachite-tag-emphasis-shadow)';
    } else if (shadow) {
        styles.boxShadow = `0 10px 15px -3px var(--malachite-tag-${type}-shadow)`;
    }

    return styles;
};

const observeLabelSize = (el: HTMLSpanElement, onResize: () => void): ResizeObserver => {
    const observer = new ResizeObserver(onResize);
    observer.observe(el);
    return observer;
};

const Tag: React.FC<TagProps> = ({
    value,
    fontSize,
    type = 'default',
    bordered = false,
    onClick,
    shadow = false,
    textTransform = 'uppercase',
    width,
    paddingSize = 'default',
    icon,
    iconPosition = 'left',
    iconColor,
    pointerType = 'default',
    onRemove,
    className = '',
}) => {
    const iconColorStyle = iconColor ? { color: getTextColor(iconColor) } : undefined;
    const observerRef = useRef<ResizeObserver | null>(null);
    const frameRef = useRef<number | null>(null);
    const [isTruncated, setIsTruncated] = useState(false);

    // Truncation applies to plain text labels; ReactNode values render untouched.
    const isTextLabel = typeof value === 'string' || typeof value === 'number';

    // Memoized so the ref keeps a stable identity. An inline ref callback would be
    // re-invoked (null, then the node) on every render, tearing down and recreating
    // the ResizeObserver each time; this way it only runs on mount/unmount.
    const setLabelRef = useCallback((el: HTMLSpanElement | null) => {
        observerRef.current?.disconnect();
        observerRef.current = null;
        if (frameRef.current !== null) {
            cancelAnimationFrame(frameRef.current);
            frameRef.current = null;
        }
        if (!el || typeof ResizeObserver === 'undefined') {
            return;
        }
        const measure = (): void => {
            frameRef.current = null;
            if (el.clientWidth === 0) {
                return;
            }
            setIsTruncated((prev) => {
                const next = el.scrollWidth > el.clientWidth;
                return prev === next ? prev : next;
            });
        };
        const check = (): void => {
            if (frameRef.current !== null) {
                return;
            }
            frameRef.current = requestAnimationFrame(measure);
        };
        check();
        observerRef.current = observeLabelSize(el, check);
    }, []);

    const handleClick = (e: React.MouseEvent) => {
        if (onClick) {
            e.stopPropagation();
            e.preventDefault();
            onClick();
        }
    };

    const baseClasses = [
        getWidthClasses(width),
        getTypeClasses(type, paddingSize, fontSize),
        textTransform === 'uppercase' ? 'uppercase' : 'normal-case',
        onClick ? `cursor-pointer ${FOCUS_RING}` : `cursor-${pointerType}`,
        isTextLabel ? 'min-w-0 max-w-full' : '',
        className,
    ]
        .filter(Boolean)
        .join(' ');

    const typeStyles = getTypeStyles(type, bordered, shadow, paddingSize);

    const tag = (
        <div
            className={baseClasses}
            onClick={handleClick}
            // No `role="button"`: it takes presentational children, so a Tag carrying an
            // `onRemove` close button would hide that button from assistive tech.
            tabIndex={onClick ? 0 : undefined}
            onKeyDown={onClick ? (event) => activateOnEnterOrSpace(event, onClick) : undefined}
            style={typeStyles}
        >
            {icon && iconPosition === 'left' && (
                <span className="mr-1 flex items-center" style={iconColorStyle}>
                    {icon}
                </span>
            )}
            {isTextLabel ? (
                <span className="flex items-center min-w-0">
                    <span key={String(value)} ref={setLabelRef} className="block min-w-0 truncate">
                        {value}
                    </span>
                </span>
            ) : (
                <span className="flex items-center">{value}</span>
            )}
            {icon && iconPosition === 'right' && (
                <span className="ml-1 flex items-center" style={iconColorStyle}>
                    {icon}
                </span>
            )}
            {onRemove && (
                <button
                    type="button"
                    aria-label={`Remove ${typeof value === 'string' ? value : ''}`}
                    onClick={(e) => {
                        e.stopPropagation();
                        onRemove(e);
                    }}
                    className="ml-0.5 flex items-center justify-center shrink-0 border-none bg-transparent text-current cursor-pointer p-0 leading-none opacity-70 hover:opacity-100"
                >
                    {/* A bare cross, not `CloseIcon`'s circled one: this sits inside the chip
                        it removes, where a second enclosing ring reads as an object of its own
                        rather than as part of the label. */}
                    <DismissIcon size={9} />
                </button>
            )}
        </div>
    );

    if (!isTruncated) {
        return tag;
    }

    return (
        <Tooltip text={value} placement="top" wrapChildren={false} isShrinkable>
            {tag}
        </Tooltip>
    );
};

export default Tag;
export { Tag };
export type { TagProps };
